import { DISCOUNT_IDS, QUALIFICATION_MESSAGES } from '@constants/apiConstants';
import {
  BroadbandSpeed,
  BundleBasketFragment,
  ChargeAmount,
  ProductType,
  Quote,
  QuoteUnavailabilityReasonType,
  Status,
} from '@generated/gql/graphql';
import { BroadbandQuote } from '@src/types/broadband';
import { parseCurrency } from '@utils/price';

import { getBroadbandInitialValues } from './getBroadbandInitialValues';

const isValidBroadbandQuote = (quote: Quote): quote is BroadbandQuote => {
  const {
    product: { type },
    status: { status, reasons },
  } = quote;

  const isBroadband = type === ProductType.Broadband;
  const isAvailable = status === Status.Available;
  const isIncomplete =
    reasons?.every(
      ({ type }) => type === QuoteUnavailabilityReasonType.Incomplete
    ) ?? false;

  return isBroadband && (isAvailable || isIncomplete);
};

const roundAvgSpeeds = (speed: BroadbandSpeed | null) => {
  const downstreamAverage = speed?.downstreamAverage ?? '0';
  return {
    ...speed,
    downstreamAverage: String(Math.round(parseFloat(downstreamAverage))),
  };
};

const mapQuotes = (quote: BroadbandQuote) => {
  const speeds = quote.product.details?.speeds?.map(
    roundAvgSpeeds
  ) as BroadbandSpeed[];
  return {
    ...quote,
    product: {
      ...quote.product,
      details: {
        ...quote.product.details,
        speeds,
      },
    },
  };
};

const formatBroadband = (basket: BundleBasketFragment) => {
  const quotes = (basket.quotes as Quote[]).reduce<BroadbandQuote[]>(
    (filtered, quote) => {
      if (!isValidBroadbandQuote(quote)) {
        return filtered;
      }
      filtered.push(quote as BroadbandQuote);
      return filtered;
    },
    []
  );
  const selectedQuote = quotes.find((quote) => quote.selected);

  const tbybEligible = !!quotes.find((quote) =>
    quote.discounts.find(
      (discount) =>
        discount.id === DISCOUNT_IDS.TBYB_BROADBAND_LINE_DISCOUNT &&
        (discount.qualified === true ||
          discount.qualificationMessage ===
            QUALIFICATION_MESSAGES.BROADBAND_MORE_SERVICES_REQUIRED)
    )
  );
  let tbybActive = false;

  let monthlyCost: ChargeAmount = {
    currency: 'GBP',
    value: '0',
  };

  if (selectedQuote) {
    tbybActive = !!selectedQuote.discounts.find(
      (d) => d.id === DISCOUNT_IDS.TBYB_BROADBAND_LINE_DISCOUNT && d.qualified
    );

    monthlyCost = parseCurrency(selectedQuote.totalMonthlyCost);
  }

  return {
    available: quotes.length > 0,
    yourService: selectedQuote
      ? getBroadbandInitialValues(selectedQuote)
      : null,
    quotes: quotes.map(mapQuotes),
    tbybEligible: tbybEligible,
    tbybActive,
    monthlyCost: monthlyCost,
  };
};

export { formatBroadband };
